/**
 * Chat WebSocket hook - manages a single WS connection per session.
 * Uses BACKEND_URL converted to ws/wss.
 */
import { useEffect, useRef, useState, useCallback } from "react";

function wsUrl() {
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const proto = backend.startsWith("https") ? "wss" : "ws";
    const host = backend.replace(/^https?:\/\//, "");
    return `${proto}://${host}/api/chat/ws`;
}

export function useChatSocket({ enabled, token, onEvent }) {
    const wsRef = useRef(null);
    const reconnectRef = useRef(null);
    const [status, setStatus] = useState("disconnected"); // connecting | connected | disconnected
    const onEventRef = useRef(onEvent);
    onEventRef.current = onEvent;

    const connect = useCallback(() => {
        if (!enabled || !token) return;
        if (wsRef.current && (wsRef.current.readyState === 0 || wsRef.current.readyState === 1)) return;
        try {
            setStatus("connecting");
            const url = `${wsUrl()}?token=${encodeURIComponent(token)}`;
            const ws = new WebSocket(url);
            wsRef.current = ws;
            ws.onopen = () => setStatus("connected");
            ws.onclose = () => {
                setStatus("disconnected");
                if (enabled) {
                    reconnectRef.current && clearTimeout(reconnectRef.current);
                    reconnectRef.current = setTimeout(() => connect(), 4000);
                }
            };
            ws.onerror = () => {
                try { ws.close(); } catch {}
            };
            ws.onmessage = (e) => {
                try {
                    const data = JSON.parse(e.data);
                    onEventRef.current?.(data);
                } catch (err) {
                    console.warn("chat ws bad json", err);
                }
            };
        } catch (err) {
            console.warn("chat connect failed", err);
            setStatus("disconnected");
        }
    }, [enabled, token]);

    useEffect(() => {
        connect();
        return () => {
            reconnectRef.current && clearTimeout(reconnectRef.current);
            try { wsRef.current?.close(); } catch {}
            wsRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, token]);

    const send = useCallback((payload) => {
        if (wsRef.current && wsRef.current.readyState === 1) {
            wsRef.current.send(JSON.stringify(payload));
            return true;
        }
        return false;
    }, []);

    return { status, send };
}
